import React, { useCallback, useMemo } from 'react';
import { IContextualMenuItem } from '@fluentui/react';
import ContextualMenuOption from '../../../Dropdown/ContextualMenu/ContextualMenuOption';
import {
  PermissionKey,
  RollingControlFunction,
} from '../../../../utils/apollo/graphqlSchema';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import {
  activeSchemaIdSelector,
  rowSelector,
} from '../../../../redux/selectors/builder';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  IRow,
  IRowMeta,
  setRowPropAction,
} from '../../../../redux/slices/oldBuilder';
import { rollingControlLabelLookup } from '../../../../utils/builder/rollingControl';
import { queryRollingControlFunctionsInfo } from '../../../KubitTooltip/globalTooltips';
import styled from 'styled-components';
import KubitContextualMenu from '../../../Dropdown/ContextualMenu';
import { SingleSelectLabel } from '../../../Dropdown/SingleSelect';

interface IProps {
  rowMeta: IRowMeta;
  onAddCohort: (() => void) | null;
  onAddRolling:
    | ((rollingControlFunction: RollingControlFunction) => void)
    | null;
  disableCohortOption: boolean;
  disableRollingOption: boolean;
}

const rollingControlOptions: RollingControlFunction[] = [
  RollingControlFunction.Avg,
  RollingControlFunction.Sum,
  RollingControlFunction.Window,
];

const Container = styled.div`
  flex: 0 0 auto;
  line-height: normal;
`;

const MeasureOptions = ({
  rowMeta,
  onAddCohort,
  onAddRolling,
  disableCohortOption,
  disableRollingOption,
}: IProps) => {
  const dispatch = useAppDispatch();

  const currentMeasure: IRow = useAppSelector((state) =>
    rowSelector(state, rowMeta),
  );

  const { showMeasure } = currentMeasure;

  const { checkIsPermitted } = usePermission();
  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const isPermittedToReadMeasure = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.MeasureRead],
    schemaId: activeSchemaId,
  });

  const handleAddNamedMeasure = useCallback(() => {
    dispatch(
      setRowPropAction({
        rowMeta,
        row: {
          showMeasure: true,
        },
      }),
    );
  }, [dispatch, rowMeta]);

  const handleAddCohortFilter = useCallback(() => {
    if (!onAddCohort) {
      return;
    }

    onAddCohort();
  }, [onAddCohort]);

  const items: IContextualMenuItem[] = useMemo(() => {
    const result: IContextualMenuItem[] = [
      {
        key: 'measure-options-named-measure',
        disabled: showMeasure,
        onRenderContent: () => (
          <ContextualMenuOption
            label={'Add Named Measure'}
            onClick={handleAddNamedMeasure}
            isUnPermitted={!isPermittedToReadMeasure}
            isDisabled={showMeasure}
            tooltipOption={{
              content: 'Add a Named Measure.',
            }}
          />
        ),
      },
    ];

    if (onAddCohort) {
      result.push({
        key: 'measure-options-cohort',
        disabled: disableCohortOption,
        onRenderContent: () => (
          <ContextualMenuOption
            label={'Add Cohort Filter'}
            onClick={handleAddCohortFilter}
            isDisabled={disableCohortOption}
            tooltipOption={{
              content:
                'Apply a cohort filter to a specific measure to analyze a unique group of users.',
            }}
          />
        ),
      });
    }

    if (onAddRolling) {
      result.push({
        key: 'measure-options-rolling',
        disabled: disableRollingOption,
        onRenderContent: () => (
          <ContextualMenuOption label={'Rolling'} hasSubMenus={true} />
        ),
        subMenuProps: {
          items: rollingControlOptions.map((rcf) => ({
            key: `measure-options-rolling-${rcf}`,
            onRenderContent: () => (
              <ContextualMenuOption
                label={rollingControlLabelLookup[rcf]}
                onClick={() => onAddRolling(rcf)}
                actionLinkAsSpan={true}
                tooltipOption={queryRollingControlFunctionsInfo[rcf]}
              />
            ),
          })),
        },
      });
    }

    return result;
  }, [
    disableCohortOption,
    disableRollingOption,
    handleAddCohortFilter,
    handleAddNamedMeasure,
    isPermittedToReadMeasure,
    showMeasure,
    onAddCohort,
    onAddRolling,
  ]);

  return (
    <Container>
      <KubitContextualMenu
        items={items}
        trigger={
          <SingleSelectLabel isInteractive={true}>
            Measure Options
          </SingleSelectLabel>
        }
        style={{
          root: {
            minWidth: '160px',
          },
        }}
      />
    </Container>
  );
};

export default MeasureOptions;
